import * as React from 'react';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Checkbox from '@mui/material/Checkbox';
import Skeleton from '@mui/material/Skeleton';
import Chip from '@mui/material/Chip';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import IconButton from '@mui/material/IconButton';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import Tooltip from '@mui/material/Tooltip';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Button } from '@mui/material';

import Config from './components/ClientConfig';
import Client from './components/Client';
import ListsProductFilters from './components/ListsProductFilters';
import noImage from './images/noImage.png';

/** If you have a top AppBar (e.g. 64px), set that value here so the sticky zone sits under it */
const STICKY_TOP_OFFSET = 48; // set to 0 if no app bar

const todayISO = () => new Date().toISOString().slice(0,10);
const formatMoney = (v) => (v === null || v === undefined)
  ? '—'
  : new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(v);

const getThumbFromProduct = (product) => {
  try {
    const imgs = product?.productImage_product || product?.images || [];
    if (!imgs.length) return null;
    let src = imgs[0]?.image;
    for (let i = 0; i < imgs.length; i++) {
      if (imgs[i]?.isMain === true) { src = imgs[i].image; break; }
    }
    if (!src) return null;
    const fileName = src.replace(/\.[^/.]+$/, "");
    const ext = src.split(".").pop();
    return `${fileName}_s.${ext}`;
  } catch { return null; }
};

// ---------- SMART CSV Export helpers (single header, normalized rows) ----------
function buildCoverageExportNormalized(coverage = [], target = { type: '', iref: '', name: '' }) {
  const rows = [];
  for (const it of (coverage || [])) {
    const company = it?.track?.company?.name || it?.track?.company?.iref || '';
    const location = it?.track?.location?.name || it?.track?.location?.iref || '';
    const list = it?.list || {};
    const price = it?.price || {};

    const { metric, value } = pickPriceMetric(price); // value, percent, amount, or cost

    rows.push({
      TargetType: safeStr(target.type),
      TargetIref: safeStr(target.iref),
      TargetName: safeStr(target.name),
      ListType: safeStr(it?.type),                  // PriceList | PromoList | CompanyList | AcquisitionList
      Company: safeStr(company),
      Location: safeStr(location),                  // stays empty when not applicable (e.g., Acquisition)
      Version: safeStr(list?.version),
      DateFrom: safeStr(list?.dateFrom),
      DateTo: safeStr(list?.dateTo),
      WithVAT: toBoolStr(it?.track?.withVAT),
      PriceMetric: metric,                          // value | percent | amount | cost
      PriceValue: value,                            // plain number or ''
      ListIref: safeStr(list?.iref),
    });
  }
  return rows;
}

function pickPriceMetric(price = {}) {
  if (price.value !== undefined && price.value !== null) return { metric: 'value', value: numOrEmpty(price.value) };
  if (price.percent !== undefined && price.percent !== null) return { metric: 'percent', value: numOrEmpty(price.percent) };
  if (price.amount !== undefined && price.amount !== null) return { metric: 'amount', value: numOrEmpty(price.amount) };
  if (price.cost !== undefined && price.cost !== null) return { metric: 'cost', value: numOrEmpty(price.cost) };
  return { metric: '', value: '' };
}

function safeStr(v) {
  if (v === null || v === undefined) return '';
  return String(v);
}
function toBoolStr(v) { return v ? 'true' : 'false'; }
function numOrEmpty(v) {
  if (v === null || v === undefined || v === '') return '';
  const n = Number(v);
  return Number.isFinite(n) ? String(n) : safeStr(v);
}

function downloadCsv(filename, rows, headerLines) {
  if (!rows?.length) return;
  const headers = Object.keys(rows[0]);
  const escape = (val) => {
    const s = val ?? '';
    const needsQuotes = /[",\n]/.test(s);
    const escaped = String(s).replace(/"/g, '""');
    return needsQuotes ? `"${escaped}"` : escaped;
  };

  const lines = [];

  // Optional custom header block (each string becomes its own line)
  if (headerLines && headerLines.length) {
    for (const line of headerLines) {
      lines.push(escape(line));
    }
    // blank line separator between header block and table header
    lines.push('');
  }

  // Normal CSV header row
  lines.push(headers.join(','));

  // Data rows
  lines.push(
    ...rows.map(r => headers.map(h => escape(r[h])).join(','))
  );

  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const STATUS_TYPES = {
  0: 'Registered',
  1: 'Idle',
  2: 'Active',
  9: 'Discontinued',
};

const getStatusLabel = (raw) => {
  if (raw === null || raw === undefined || raw === '') return '—';
  const n = Number(raw);
  if (Object.prototype.hasOwnProperty.call(STATUS_TYPES, n)) {
    return STATUS_TYPES[n];
  }
  return String(raw);
};

const PRODUCTS_ENDPOINT = (Config.API_URL_PRODUCT);
const PRODUCT_COVERAGE_ENDPOINT = () => (`${Config.API_URL_PRODUCT}product_coverage/`);

function PriceRow({
  name='—',
  location='',
  effective='—',
  version='—',
  iref='—',
  active=false,
  withVAT=false,
  value=null,
  skeleton=false,
  hasCompany=false,
  isAcquisition=false
}) {
  if (skeleton) {
    return (
      <TableRow>
        <TableCell sx={{ width: 260 }}>
          {hasCompany ? <Skeleton variant="text" width={200} /> : (isAcquisition ? null : <Skeleton variant="text" width={200} />)}
        </TableCell>
        <TableCell sx={{ width: 240 }}>
          {(hasCompany || isAcquisition) ? <Skeleton variant="text" width={160} /> : null}
        </TableCell>
        <TableCell><Skeleton variant="text" width={180} /></TableCell>
        <TableCell><Skeleton variant="text" width={60} /></TableCell>
        <TableCell><Skeleton variant="rectangular" width={20} height={20} /></TableCell>
        <TableCell><Skeleton variant="rectangular" width={20} height={20} /></TableCell>
        <TableCell align="right"><Skeleton variant="text" width={70} sx={{ ml: 'auto' }}/></TableCell>
      </TableRow>
    );
  }
  return (
    <TableRow hover>
      <TableCell sx={{ width: 260 }}>
        {hasCompany ? location : (isAcquisition ? null : name)}
      </TableCell>
      <TableCell sx={{ width: 240 }}>
        {(hasCompany || isAcquisition) ? name : null}
      </TableCell>
      <TableCell align="right" sx={{ width: 160 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5, flexWrap: 'wrap' }}>
          <Chip label={`v${version}`} size="small" color="primary" />
          <Chip label={`${iref}`} size="small" variant="outlined" />
        </Box>
      </TableCell>
      <TableCell align="right" sx={{ width: 200 }}>{effective}</TableCell>
      <TableCell align="right" sx={{ width: 100 }}>
        <Checkbox color="success" checked={!!active} readOnly />
      </TableCell>
      <TableCell align="right" sx={{ width: 110 }}>
        <Checkbox color="success" checked={!!withVAT} readOnly />
      </TableCell>
      <TableCell align="right" sx={{ width: 120 }}>{formatMoney(value)}</TableCell>
    </TableRow>
  );
}

function sortByName(arr = []) {
  return [...arr].sort((a, b) => {
    const an = (a?.name || '').toString();
    const bn = (b?.name || '').toString();
    const byName = an.localeCompare(bn, 'ro', { sensitivity: 'base', numeric: true });
    if (byName !== 0) return byName;
    const asku = (a?.sku || '').toString();
    const bsku = (b?.sku || '').toString();
    const bySku = asku.localeCompare(bsku, 'ro', { sensitivity: 'base', numeric: true });
    if (bySku !== 0) return bySku;
    return (a?.iref || '').toString().localeCompare((b?.iref || '').toString(), 'ro', { numeric: true });
  });
}

export default function ListsProduct(){
  const [client] = React.useState(() => new Client('none'));
  const [products, setProducts] = React.useState([]);
  const [selectedProduct, setSelectedProduct] = React.useState(null);
  const [query, setQuery] = React.useState(() => {
    const saved = JSON.parse(localStorage.getItem('listsProduct.query') || '{}');
    return { date: saved.date || todayISO(), only_with_price: saved.only_with_price ?? 1 };
  });
  const [coverage, setCoverage] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    const res = client.getData(PRODUCTS_ENDPOINT, { orderBy_Name: true });
    const list = unwrapProducts(res);
    const sorted = sortByName(list);
    setProducts(sorted);

    const savedIref = localStorage.getItem('listsProduct.productIref');
    if (savedIref && sorted?.length) {
      const found = sorted.find(p => String(p.iref) === String(savedIref));
      if (found) setSelectedProduct(found);
    }
  }, [client]);

  React.useEffect(() => {
    localStorage.setItem('listsProduct.query', JSON.stringify(query));
  }, [query]);
  React.useEffect(() => {
    if (selectedProduct?.iref) {
      localStorage.setItem('listsProduct.productIref', String(selectedProduct.iref));
    }
  }, [selectedProduct]);

  React.useEffect(() => {
    if (!selectedProduct?.iref) {
      setCoverage([]);
      return;
    }
    setLoading(true);
    try {
      const data = client.getData(PRODUCT_COVERAGE_ENDPOINT(), {
        product: selectedProduct.iref,
        date: query.date || todayISO(),
        only_with_price: query.only_with_price ? 1 : 0,
      });
      setCoverage(Array.isArray(data?.items) ? data.items : []);
    } catch {
      setCoverage([]);
    } finally {
      setLoading(false);
    }
  }, [client, selectedProduct, query]);

  const productThumb = getThumbFromProduct(selectedProduct) || noImage;
  const byType = (t) => coverage.filter(it => it.type === t);

  const priceListRows = toRows(byType('PriceList'), (it) => ({
    name: it.track?.location?.name || it.track?.location?.iref || '—',
    effective: formatEffective(it.list),
    version: safeVersion(it.list),
    iref: it.list?.iref || '—',
    withVAT: !!it.track?.withVAT,
    value: (it.price && (it.price.value ?? it.price.cost)) ?? null,
  }));

  const promoListRows = toRows(byType('PromoList'), (it) => ({
    name: it.track?.location?.name || it.track?.location?.iref || '—',
    effective: formatEffective(it.list),
    version: safeVersion(it.list),
    iref: it.list?.iref || '—',
    withVAT: !!it.track?.withVAT,
    value: (it.price && (it.price.value ?? it.price.percent ?? it.price.amount)) ?? null,
  }));

  // Company Lists: both company + location
  const companyListRows = toRows(byType('CompanyList'), (it) => ({
    name: it.track?.company?.name || it.track?.company?.iref || '—',
    location: it.track?.location?.name || it.track?.location?.iref || '—',
    effective: formatEffective(it.list),
    version: safeVersion(it.list),
    iref: it.list?.iref || '—',
    withVAT: !!it.track?.withVAT,
    value: (it.price && (it.price.value ?? it.price.cost)) ?? null,
    hasCompany: true,
  }));

  const acquisitionListRows = toRows(byType('AcquisitionList'), (it) => ({
    name: it.track?.company?.name || it.track?.company?.iref || '—',
    effective: formatEffective(it.list),
    version: safeVersion(it.list),
    iref: it.list?.iref || '—',
    withVAT: !!it.track?.withVAT,
    value: (it.price && (it.price.cost ?? it.price.value)) ?? null,
  }));

  const counts = {
    price: priceListRows.length,
    promo: promoListRows.length,
    company: companyListRows.length,
    acq: acquisitionListRows.length,
  };

  return (
    <Box sx={{ backgroundColor: '#273442', minHeight: '100%', pb: 4 }}>
      {/* Sticky top area: filters + product summary */}
      <Box
        sx={{
          position: 'sticky',
          top: STICKY_TOP_OFFSET, // ensure it's fully visible under your app bar
          zIndex: 20,             // above tables/app content
          px: 2,
          pt: 0.25,
          pb: 0.5,
          backdropFilter: 'blur(6px)',
          background: 'linear-gradient(180deg, rgba(39,52,66,0.95) 0%, rgba(39,52,66,0.7) 100%)',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          boxShadow: '0 6px 16px rgba(0,0,0,0.35)',
          borderRadius: 2
        }}
      >
        <ListsProductFilters
          products={products}
          selectedProduct={selectedProduct}
          dateValue={query.date}
          onlyWithPrice={!!query.only_with_price}
          onChange={(payload) => {
            if (payload.product !== undefined) setSelectedProduct(payload.product);
            if (payload.date !== undefined) setQuery(q => ({ ...q, date: payload.date }));
            if (payload.only_with_price !== undefined) setQuery(q => ({ ...q, only_with_price: payload.only_with_price ? 1 : 0 }));
          }}
        />

        {selectedProduct && (
          <Card sx={{ mt: 0.5, backgroundColor: '#214a80', borderRadius: 2 }}>
            <CardContent sx={{ py: 0.75 }}>
              <Box sx={{ display:'flex', alignItems:'center', gap:1, flexWrap: 'wrap' }}>
                <Box component="img" sx={{ height:56, width:56, border:'1px dashed grey', borderRadius: 1 }} alt="No Image" src={productThumb} />
                <Box sx={{ minWidth: 240 }}>
                  <Typography variant="subtitle1" sx={{ color:'#e3e3e5', lineHeight: 1.25 }}>
                    {selectedProduct?.name || '—'}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color:'#e3e3e5' }}>
                    <Typography variant="caption">SKU: {selectedProduct?.sku || '—'} • IREF: {selectedProduct?.iref || '—'}</Typography>
                    {selectedProduct?.iref && (
                      <Tooltip title="Copy IREF">
                        <IconButton
                          size="small"
                          onClick={() => navigator.clipboard?.writeText(String(selectedProduct.iref))}
                          sx={{ color: '#e3e3e5' }}
                        >
                          <ContentCopyIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Box>
                {/* High-contrast summary chips */}
                <Box sx={{ display:'flex', gap:1, flexWrap:'wrap', ml: 'auto' }}>
                  <Chip
                    size="small"
                    label={`Status: ${getStatusLabel(selectedProduct?.status)}`}
                    sx={{
                      color: '#e3e3e5',
                      borderColor: 'rgba(227,227,229,0.28)',
                      backgroundColor: 'rgba(255,255,255,0.06)',
                      fontWeight: 600,
                    }}
                  />
                  <Chip
                    size="small"
                    label={`For sale: ${selectedProduct?.isForSale ? 'Yes' : 'No'}`}
                    sx={{
                      color: '#e3e3e5',
                      borderColor: 'rgba(227,227,229,0.28)',
                      backgroundColor: 'rgba(255,255,255,0.06)',
                      fontWeight: 600,
                    }}
                  />
                  <Chip
                    size="small"
                    label={`Sold alone: ${selectedProduct?.isSoldAlone ? 'Yes' : 'No'}`}
                    sx={{
                      color: '#e3e3e5',
                      borderColor: 'rgba(227,227,229,0.28)',
                      backgroundColor: 'rgba(255,255,255,0.06)',
                      fontWeight: 600,
                    }}
                  />
                  <Chip
                    size="small"
                    label={`Purchased: ${selectedProduct?.isPurchased ? 'Yes' : 'No'}`}
                    sx={{
                      color: '#e3e3e5',
                      borderColor: 'rgba(227,227,229,0.28)',
                      backgroundColor: 'rgba(255,255,255,0.06)',
                      fontWeight: 600,
                    }}
                  />
                  <Chip size="small" label={`Price: ${counts.price}`} sx={{ color: '#e3e3e5', borderColor: 'rgba(227,227,229,0.28)', backgroundColor: 'rgba(255,255,255,0.06)', fontWeight: 600 }} />
                  <Chip size="small" label={`Promos: ${counts.promo}`} sx={{ color: '#e3e3e5', borderColor: 'rgba(227,227,229,0.28)', backgroundColor: 'rgba(255,255,255,0.06)', fontWeight: 600 }}/>
                  <Chip size="small" label={`Company: ${counts.company}`} sx={{ color: '#e3e3e5', borderColor: 'rgba(227,227,229,0.28)', backgroundColor: 'rgba(255,255,255,0.06)', fontWeight: 600 }} />
                  <Chip size="small" label={`Acquisition: ${counts.acq}`} sx={{ color: '#e3e3e5', borderColor: 'rgba(227,227,229,0.28)', backgroundColor: 'rgba(255,255,255,0.06)', fontWeight: 600 }} />
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<CloudDownloadIcon />}
                    onClick={() => {
                      const target = {
                        type: 'product',
                        iref: selectedProduct?.iref,
                        name: selectedProduct?.name,
                      };

                      const rows = buildCoverageExportNormalized(coverage, target);

                      // Separate header block (each string = one line above the table)
                      const headerLines = [
                        'Product Coverage Export',
                        `Product: ${selectedProduct?.name || ''}`,
                        `SKU: ${selectedProduct?.sku || ''}`,
                        `IREF: ${selectedProduct?.iref || ''}`,
                        `Status: ${getStatusLabel(selectedProduct?.status)}`,
                        `For sale: ${selectedProduct?.isForSale ? 'Yes' : 'No'}`,
                        `Sold alone: ${selectedProduct?.isSoldAlone ? 'Yes' : 'No'}`,
                        `Date: ${query?.date || todayISO()}`,
                      ];

                      const safeName = (selectedProduct?.sku || selectedProduct?.name || selectedProduct?.iref || 'coverage')
                        .toString()
                        .replace(/[^\w\-]+/g, '_');

                      downloadCsv(
                        `product_${safeName}_${(query?.date || '').replaceAll('-','')}.csv`,
                        rows,
                        headerLines
                      );
                    }}
                    sx={{ ml: 1 }}
                  >
                    Export CSV
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>
        )}
      </Box>

      {/* Page body */}
      <Box sx={{ px: 2, pt: 2, pb: 4 }}>
        {selectedProduct ? (
          <React.Fragment>
            <CoverageTable title="Price Lists" nameLabel="Location name" rows={priceListRows} loading={loading} valueLabel="Value" />
            <CoverageTable title="Promotion Lists" nameLabel="Location name" rows={promoListRows} loading={loading} valueLabel="Value" />
            <CoverageTable title="Company Lists" rows={companyListRows} loading={loading} valueLabel="Value" hasCompany />
            <CoverageTable title="Acquisition Lists" nameLabel="Company name" rows={acquisitionListRows} loading={loading} valueLabel="Cost" isAcquisition/>
          </React.Fragment>
        ) : (
          <EmptyHint />
        )}
      </Box>
    </Box>
  );
}

/* ===== Helpers ===== */
function unwrapProducts(res){
  if (!res) return [];
  if (Array.isArray(res)) return res;
  return res.products || res.items || res.results || res.data || res.pcPriceLists || [];
}
function toRows(items, map){
  return items.map(it => ({
    ...map(it),
    // reflect the PRODUCT LINE status coming from the endpoint
    active: !!(it?.price?.isActive ?? it?.price?.active ?? it?.price?.enabled),
  }));
}
function formatEffective(list){
  const from = list?.dateFrom || '';
  const to = list?.dateTo || '∞';
  return `${from} → ${to}`;
}
function safeVersion(list){
  const v = list?.version;
  return (v === null || v === undefined) ? '—' : String(v);
}

function CoverageTable({ title, nameLabel, rows, loading=false, valueLabel='Value', hasCompany=false, isAcquisition=false }){
  const skeletonRows = Array.from({ length: 5 }).map((_, i) => <PriceRow key={`sk_${i}`} skeleton hasCompany={hasCompany} isAcquisition={!!isAcquisition} />);

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="body1" gutterBottom sx={{ color:'#e3e3e5', px: 0.5 }}>{title}</Typography>
      <TableContainer component={Paper} sx={{ borderRadius: 1 }}>
        <Table size="small" stickyHeader sx={{ backgroundColor:'#eeeee4' }}>
          <TableHead sx={{ backgroundColor:'#ddddd4' }}>
            <TableRow>
              <TableCell align="left" sx={{ width: 240, fontWeight: 600 }}>
                {hasCompany ? 'Location name' : (isAcquisition ? '' : nameLabel)}
              </TableCell>
              <TableCell align="left" sx={{ width: 260, fontWeight: 600 }}>
                {hasCompany || isAcquisition ? 'Company name' : ''}
              </TableCell>
              <TableCell align="center" sx={{ width: 160, fontWeight: 600 }}>Version / Iref</TableCell>
              <TableCell align="center" sx={{ width: 200, fontWeight: 600 }}>Effective</TableCell>
              <TableCell align="right" sx={{ width: 100, fontWeight: 600 }}>Active</TableCell>
              <TableCell align="right" sx={{ width: 110, fontWeight: 600 }}>With VAT</TableCell>
              <TableCell align="right" sx={{ width: 120, fontWeight: 600 }}>{valueLabel}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? skeletonRows : rows.map((r, i) => <PriceRow key={`${title}_${i}`} {...r} hasCompany={hasCompany} isAcquisition={!!isAcquisition} />)}
            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" sx={{ color: '#6b6b6b', py: 1.5 }}>No entries for the current filters.</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

function EmptyHint(){
  return (
    <Card sx={{ mt: 3, backgroundColor: '#214a80', borderRadius: 2 }}>
      <CardContent>
        <Typography variant="h6" sx={{ color:'#e3e3e5', mb: 0.5 }}>Pick a product to see coverage</Typography>
        <Typography variant="body2" sx={{ color:'#e3e3e5', opacity: 0.9 }}>
          Use the product search above. Coverage tables will load automatically for the selected date.
        </Typography>
      </CardContent>
    </Card>
  );
}
